import { Fragment } from 'react';
import { Box, Tooltip } from '@mui/material';
import _ from 'lodash';

import { convertDateTimeIntoTimezone, readableBytesFormat } from '../../../utils/util';
import { Action, FLOOR_MAX_CONSECUTIVE_WINDOWS, FLOOR_RECOVERY_DEADBAND_PERCENT, Quota, windowLengthSeconds } from '../types';
import { FloorUsageTooltip } from './FloorUsageTooltip';
import './index.css';

interface FloorUsageBarProps {
    quota: Quota;
    userTimezone: string;
}

// a counted window that moved nothing is the dead-link case, so it reads 0 B, never "-"
export const floorBytes = (bytes: number): string => bytes > 0 ? readableBytesFormat(bytes) : '0 B';

export const floorTiers = (quota: Quota): Action[] =>
    _.orderBy((quota.actions ?? []).filter(a => !a.revert), a => a.usage_percentage, 'desc');

// the tier closest to firing among those still building a run; the counter keeps climbing
// past a fired tier's run (executed stops the refire), so a complete run is excluded
const tierNearestFiring = (tiers: Action[]): Action | undefined =>
    _.maxBy(
        tiers.filter(a => !a.executed && (a.breach_window_count ?? 0) > 0 && (a.breach_window_count ?? 0) < (a.consecutive_windows ?? 1)),
        a => (a.breach_window_count ?? 0) / Math.max(1, a.consecutive_windows ?? 1)
    );

export const floorWindowRange = (quota: Quota, userTimezone: string): string => {
    if (!quota.last_window_end) return '';
    const end = new Date(quota.last_window_end);
    const start = new Date(end.getTime() - windowLengthSeconds(quota) * 1000);
    const fmt = (d: Date) => convertDateTimeIntoTimezone(d.toISOString(), userTimezone, 'HH:mm');
    return `${fmt(start)}–${fmt(end)}`;
}

function Squares(props: { filled: number, total: number, tone: 'alert' | 'exceed' | 'ok', label: string }) {
    const { filled, total, tone, label } = props;
    // past the ring size the text still carries the exact count
    const shown = Math.min(total, FLOOR_MAX_CONSECUTIVE_WINDOWS);
    return (
        <Box className='floor_usage--pips'>
            {_.range(shown).map(i => <span key={i} className={`floor_usage--pip ${i < Math.min(filled, shown) ? `floor_usage--pip--${tone}` : ''}`} />)}
            <span className='floor_usage--pips_text'>{label}</span>
        </Box>
    );
}

export function FloorUsageBar(props: FloorUsageBarProps) {
    const { quota, userTimezone } = props;

    const status = quota.status ?? '';
    const suppressed = status === 'unknown';
    const used = Number(quota.current_usage_updated ?? quota.current_usage ?? 0);
    const limit = Number(quota.limit);
    const percent = limit > 0 ? Math.round(used / limit * 100) : 0;
    const tiers = floorTiers(quota);
    const range = floorWindowRange(quota, userTimezone);

    const fillTone = status === 'exceeded' ? 'exceed' : status === 'alerting' ? 'alert' : 'ok';
    // a recovering link is back above its minimum even while its status still reads exceeded
    const recovering = status === 'exceeded' && (quota.healthy_window_count ?? 0) > 0;
    const barTone = recovering ? 'ok' : fillTone;

    const nearest = tierNearestFiring(tiers);

    return (
        <Fragment>
            {/* the title renders only while hovered, so the chart costs nothing for the list */}
            <Tooltip title={<FloorUsageTooltip quota={quota} userTimezone={userTimezone} />} classes={{ tooltip: 'quota_management--usage_tooltip' }} placement='top'>
            <Box className='floor_usage--bar_wrap'>
                <Box className={`floor_usage--bar ${suppressed ? 'floor_usage--bar--suppressed' : ''}`}>
                    {!suppressed && <Box className={`floor_usage--fill floor_usage--fill--${barTone}`} sx={{ width: `${Math.min(percent, 100)}%` }} />}
                    {_.uniq(tiers.map(t => t.usage_percentage)).filter(p => p > 0 && p < 100).map(p =>
                        <Box key={p} className='floor_usage--tick' sx={{ left: `${p}%` }}><span>{p}</span></Box>
                    )}
                    <Box className='floor_usage--floor_mark' />
                </Box>
            </Box>
            </Tooltip>
            <Box>
                {suppressed
                    ? `— / ${floorBytes(limit)} min`
                    : `${floorBytes(used)} / ${floorBytes(limit)} min (${percent}%)`}
                {range && <span className='floor_usage--range'> · {range}</span>}
            </Box>
            {!suppressed && recovering &&
                <Squares filled={quota.healthy_window_count ?? 0} total={quota.recovery_windows ?? 2} tone='ok'
                    label={`Recovering · ${quota.healthy_window_count} of ${quota.recovery_windows ?? 2} windows ≥ ${FLOOR_RECOVERY_DEADBAND_PERCENT}%`} />}
            {!suppressed && !recovering && nearest &&
                <Squares filled={nearest.breach_window_count ?? 0} total={nearest.consecutive_windows ?? 1} tone={fillTone === 'ok' ? 'alert' : fillTone}
                    label={`${nearest.breach_window_count} of ${nearest.consecutive_windows ?? 1} windows ≤ ${nearest.usage_percentage}%`} />}
        </Fragment>
    );
}
